import React, { useState, useEffect, useCallback } from 'react';
import { istDate } from '../lib/ist';
import axios from 'axios';
import { UserPlus, Trash2, Shield, User, Settings2, Save, AlertTriangle, RefreshCw, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const PERMISSION_MODULES = [
  { key: 'dashboard', label: 'Dashboard', allowed: ['none', 'view'] },
  { key: 'analytics', label: 'Analytics & Profitability', allowed: ['none', 'view'] },
  { key: 'booths', label: 'Booths & Fleet', allowed: ['none', 'view', 'edit'] },
  { key: 'payments', label: 'Payments & Late Prints', allowed: ['none', 'view', 'edit'] },
  { key: 'vouchers', label: 'Vouchers', allowed: ['none', 'view', 'edit'] },
  { key: 'leads', label: 'Leads', allowed: ['none', 'view', 'edit'] },
  { key: 'software', label: 'Software', allowed: ['none', 'view'] },
];

const DEFAULT_ADMIN_PERMS = {
  dashboard: 'view',
  booths: 'view',
  analytics: 'view',
  payments: 'view',
  leads: 'view',
  users: 'none',
  software: 'view',
  vouchers: 'view',
};

const PERM_PRESETS = [
  { id: 'analytics_viewer', label: 'Analytics Viewer', hint: 'Analytics only — nothing else',
    perms: { dashboard: 'none', analytics: 'view', booths: 'none', payments: 'none', vouchers: 'none', leads: 'none', software: 'none', users: 'none' } },
  { id: 'operator', label: 'Booth Operator', hint: 'Dashboard + booths (view) + software',
    perms: { dashboard: 'view', analytics: 'none', booths: 'view', payments: 'view', vouchers: 'none', leads: 'none', software: 'view', users: 'none' } },
  { id: 'viewer_all', label: 'Viewer (everything)', hint: 'View-only access to all modules',
    perms: { dashboard: 'view', analytics: 'view', booths: 'view', payments: 'view', vouchers: 'view', leads: 'view', software: 'view', users: 'none' } },
  { id: 'manager', label: 'Manager', hint: 'Full access except user management',
    perms: { dashboard: 'view', analytics: 'view', booths: 'edit', payments: 'edit', vouchers: 'edit', leads: 'edit', software: 'view', users: 'none' } },
];

const matchPreset = (perms) => {
  for (const p of PERM_PRESETS) {
    if (PERMISSION_MODULES.every((m) => (perms[m.key] || 'none') === p.perms[m.key])) return p.id;
  }
  return 'custom';
};

const AdminUsers = () => {
  const [admins, setAdmins] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', role: 'admin', preset: 'viewer_all', assigned_kiosks: [], share_percent: '' });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editAssigned, setEditAssigned] = useState([]);
  const [editShare, setEditShare] = useState('');
  const [savingId, setSavingId] = useState(null);

  // System reset (3-step verification)
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetAck, setResetAck] = useState(false);
  const [resetWipeAdmins, setResetWipeAdmins] = useState(false);
  const [resetPhrase, setResetPhrase] = useState('');
  const [resetEmailConfirm, setResetEmailConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const { token, user } = useAuth();

  const closeReset = () => {
    setResetOpen(false);
    setResetStep(1);
    setResetAck(false);
    setResetWipeAdmins(false);
    setResetPhrase('');
    setResetEmailConfirm('');
  };

  const performReset = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API}/system/reset`,
        {
          confirmation_text: resetPhrase,
          email_confirmation: resetEmailConfirm,
          wipe_admins: resetWipeAdmins,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const totals = Object.entries(res.data.wiped || {}).reduce((a, [_, v]) => a + (v || 0), 0);
      toast.success(`System reset complete — ${totals} records wiped`);
      closeReset();
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setResetting(false);
  };

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    }
    setLoading(false);
  }, [token]);

  const fetchKiosks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kiosks`, { headers: { Authorization: `Bearer ${token}` } });
      setKiosks(res.data.map((k) => ({ id: k.id, name: k.name })));
    } catch {
      // ignore
    }
  }, [token]);

  useEffect(() => {
    fetchAdmins();
    fetchKiosks();
  }, [fetchAdmins, fetchKiosks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const preset = PERM_PRESETS.find((p) => p.id === newAdmin.preset);
      const payload = {
        email: newAdmin.email,
        role: newAdmin.role,
        assigned_kiosks: newAdmin.assigned_kiosks,
      };
      if (newAdmin.role === 'admin' && preset) payload.permissions = preset.perms;
      if (newAdmin.role === 'partner') payload.share_percent = parseFloat(newAdmin.share_percent || 0);
      await axios.post(`${API}/admins`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setShowCreateModal(false);
      setNewAdmin({ email: '', role: 'admin', preset: 'viewer_all', assigned_kiosks: [], share_percent: '' });
      fetchAdmins();
      toast.success(
        newAdmin.role === 'partner'
          ? 'Partner invited — they can sign in with Google using this email and will only see their booths'
          : 'Admin invited — they can now sign in with Google using this email'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    }
    setCreating(false);
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Delete this admin user?')) return;
    try {
      await axios.delete(`${API}/admins/${adminId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAdmins();
      toast.success('Admin deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const startEdit = (admin) => {
    setEditingId(admin.id);
    setEditPerms({ ...DEFAULT_ADMIN_PERMS, ...(admin.permissions || {}) });
    setEditAssigned(admin.assigned_kiosks || []);
    setEditShare(admin.share_percent ?? 0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditPerms({});
    setEditAssigned([]);
  };

  const savePermissions = async (adminId) => {
    setSavingId(adminId);
    const target = admins.find((a) => a.id === adminId);
    try {
      if (target?.role === 'partner') {
        await axios.put(`${API}/admins/${adminId}/partner-settings`,
          { share_percent: parseFloat(editShare || 0) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(`${API}/admins/${adminId}/permissions`,
          { permissions: editPerms },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await axios.put(`${API}/admins/${adminId}/assigned-kiosks`,
        { assigned_kiosks: editAssigned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(target?.role === 'partner' ? 'Partner settings updated' : 'Permissions updated');
      cancelEdit();
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save permissions');
    }
    setSavingId(null);
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-charcoal-800/20 mb-4" />
        <h2 className="font-serif text-2xl text-charcoal-900 mb-2">Access Denied</h2>
        <p className="text-charcoal-800/60">Only super admins can manage users</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">Admin Users</h1>
          <p className="text-charcoal-800/60 mt-1 text-sm sm:text-base">{admins.length} users</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="create-admin-btn"
        >
          <UserPlus className="w-4 h-4" />
          Add Admin
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="card-dashboard animate-pulse">
              <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-24 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {admins.map((admin) => {
            const isEditing = editingId === admin.id;
            const isSelf = admin.id === user.id;
            return (
              <div key={admin.id} className="card-dashboard" data-testid={`admin-${admin.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-4">
                    {admin.picture ? (
                      <img
                        src={admin.picture}
                        alt=""
                        className="w-12 h-12 rounded-xl object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        admin.role === 'super_admin' ? 'bg-amber-100' : admin.role === 'partner' ? 'bg-emerald-100' : 'bg-rose-100'
                      }`}>
                        {admin.role === 'super_admin' ? (
                          <Shield className="w-6 h-6 text-amber-700" />
                        ) : admin.role === 'partner' ? (
                          <Percent className="w-6 h-6 text-emerald-700" />
                        ) : (
                          <User className="w-6 h-6 text-charcoal-900" />
                        )}
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-charcoal-900" data-testid={`admin-email-${admin.id}`}>
                        {admin.name || admin.email}
                        {isSelf && <span className="ml-2 text-xs text-charcoal-800/40">(you)</span>}
                      </h3>
                      {admin.name && (
                        <p className="text-xs text-charcoal-800/50">{admin.email}</p>
                      )}
                      <p className="text-sm text-charcoal-800/60 capitalize">
                        {admin.role.replace('_', ' ')}
                        {admin.role === 'partner' && (
                          <span className="ml-2 text-xs font-medium text-emerald-700 normal-case" data-testid={`partner-share-${admin.id}`}>
                            {admin.share_percent ?? 0}% revenue share · {(admin.assigned_kiosks || []).length} booth{(admin.assigned_kiosks || []).length === 1 ? '' : 's'}
                          </span>
                        )}
                        {admin.last_login_at && (
                          <span className="ml-2 text-xs text-charcoal-800/40">
                            · last signed in {istDate(admin.last_login_at)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {admin.role !== 'super_admin' && !isEditing && (
                      <button
                        onClick={() => startEdit(admin)}
                        className="px-3 py-2 text-sm text-charcoal-800/70 hover:text-charcoal-900 border border-charcoal-900/10 rounded-lg hover:bg-cream-50 transition-colors flex items-center gap-1.5"
                        data-testid={`edit-perms-${admin.id}`}
                      >
                        <Settings2 className="w-4 h-4" />
                        Permissions
                      </button>
                    )}
                    {admin.role !== 'super_admin' && (
                      <button
                        onClick={() => handleDelete(admin.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-admin-${admin.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline permission matrix */}
                {isEditing && (
                  <div className="mt-5 pt-5 border-t border-charcoal-900/5" data-testid={`perms-editor-${admin.id}`}>
                    {admin.role === 'partner' ? (
                      <div className="mb-5">
                        <h4 className="text-sm font-medium text-charcoal-900 mb-2">Revenue share %</h4>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={editShare}
                          onChange={(e) => setEditShare(e.target.value)}
                          className="w-32 px-4 py-2.5 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                          data-testid={`edit-share-${admin.id}`}
                        />
                        <p className="text-xs text-charcoal-800/50 mt-2">
                          Their dashboard shows this % of net revenue (gross minus refunds) for the booths below.
                        </p>
                      </div>
                    ) : (
                    <>
                    <h4 className="text-sm font-medium text-charcoal-900 mb-2">Quick presets</h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {PERM_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setEditPerms({ ...editPerms, ...p.perms })}
                          title={p.hint}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            matchPreset(editPerms) === p.id
                              ? 'bg-charcoal-900 text-white border-charcoal-900'
                              : 'bg-white text-charcoal-800/70 border-charcoal-900/10 hover:bg-cream-50'
                          }`}
                          data-testid={`preset-${admin.id}-${p.id}`}
                        >
                          {p.label}
                        </button>
                      ))}
                      {matchPreset(editPerms) === 'custom' && (
                        <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800" data-testid={`preset-${admin.id}-custom`}>Custom</span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-charcoal-900 mb-3">Module access</h4>
                    <div className="space-y-2 mb-5">
                      {PERMISSION_MODULES.map((mod) => (
                        <div key={mod.key} className="flex items-center justify-between gap-3">
                          <span className="text-sm text-charcoal-800">{mod.label}</span>
                          <div className="flex gap-1.5">
                            {mod.allowed.map((level) => (
                              <button
                                key={level}
                                onClick={() => setEditPerms({ ...editPerms, [mod.key]: level })}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors capitalize ${
                                  editPerms[mod.key] === level
                                    ? level === 'edit'
                                      ? 'bg-charcoal-900 text-white'
                                      : level === 'view'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-charcoal-200 text-charcoal-800'
                                    : 'bg-cream-50 text-charcoal-800/60 hover:bg-cream-100'
                                }`}
                                data-testid={`perm-${admin.id}-${mod.key}-${level}`}
                              >
                                {level}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    </>
                    )}

                    <h4 className="text-sm font-medium text-charcoal-900 mb-3">Assigned booths</h4>
                    <p className="text-xs text-charcoal-800/50 mb-3">
                      If unset, this admin sees no booths. Used for analytics, dashboard, and booth lists.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                      {kiosks.length === 0 && (
                        <p className="text-sm text-charcoal-800/40">No booths created yet.</p>
                      )}
                      {kiosks.map((k) => {
                        const checked = editAssigned.includes(k.id);
                        return (
                          <label key={k.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-charcoal-900/10 cursor-pointer hover:bg-cream-50">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) setEditAssigned([...editAssigned, k.id]);
                                else setEditAssigned(editAssigned.filter((x) => x !== k.id));
                              }}
                              className="accent-charcoal-900"
                              data-testid={`assigned-${admin.id}-${k.id}`}
                            />
                            <span className="text-sm text-charcoal-900 truncate">{k.name}</span>
                          </label>
                        );
                      })}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={cancelEdit}
                        className="flex-1 px-4 py-2.5 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => savePermissions(admin.id)}
                        disabled={savingId === admin.id}
                        className="flex-1 px-4 py-2.5 bg-charcoal-900 text-white rounded-xl hover:bg-charcoal-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid={`save-perms-${admin.id}`}
                      >
                        <Save className="w-4 h-4" />
                        {savingId === admin.id ? 'Saving...' : 'Save permissions'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl" data-testid="create-admin-modal">
            <h3 className="font-serif text-2xl text-charcoal-900 mb-2">Invite Admin</h3>
            <p className="text-sm text-charcoal-800/60 mb-6">
              They&rsquo;ll sign in with Google using this exact email address.
              No password is created — nobody else can log in as them.
            </p>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal-800 mb-2">Google Email</label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  required
                  maxLength={254}
                  placeholder="e.g. venue-manager@piczoo.in"
                  className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-800 mb-2">Role</label>
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                  className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                  data-testid="admin-role-select"
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="partner">Partner (revenue share)</option>
                </select>
              </div>

              {newAdmin.role === 'partner' && (
                <div>
                  <label className="block text-sm font-medium text-charcoal-800 mb-2">Revenue share %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    required
                    value={newAdmin.share_percent}
                    onChange={(e) => setNewAdmin({ ...newAdmin, share_percent: e.target.value })}
                    placeholder="e.g. 30"
                    className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                    data-testid="partner-share-input"
                  />
                  <p className="text-xs text-charcoal-800/50 mt-2">
                    They see ONLY their booths: net revenue (after refunds), their share, and a monthly payout statement. No access to any admin page.
                  </p>
                </div>
              )}

              {newAdmin.role === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal-800 mb-2">Access level</label>
                    <select
                      value={newAdmin.preset}
                      onChange={(e) => setNewAdmin({ ...newAdmin, preset: e.target.value })}
                      className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                      data-testid="admin-preset-select"
                    >
                      {PERM_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-charcoal-800/50 mt-2">
                      {PERM_PRESETS.find((p) => p.id === newAdmin.preset)?.hint}. You can fine-tune each module after inviting.
                    </p>
                  </div>
              )}

              {newAdmin.role !== 'super_admin' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal-800 mb-2">Assigned booths</label>
                    <p className="text-xs text-charcoal-800/50 mb-2">Data they can see is limited to these booths. Leave empty = no booths visible.</p>
                    <div className="max-h-36 overflow-y-auto space-y-1.5 border border-charcoal-900/10 rounded-xl p-3 bg-cream-50/50">
                      {kiosks.length === 0 && <p className="text-xs text-charcoal-800/40">No booths yet.</p>}
                      {kiosks.map((k) => {
                        const checked = newAdmin.assigned_kiosks.includes(k.id);
                        return (
                          <label key={k.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => setNewAdmin({
                                ...newAdmin,
                                assigned_kiosks: e.target.checked
                                  ? [...newAdmin.assigned_kiosks, k.id]
                                  : newAdmin.assigned_kiosks.filter((x) => x !== k.id),
                              })}
                              className="accent-charcoal-900"
                              data-testid={`invite-assigned-${k.id}`}
                            />
                            <span className="text-sm text-charcoal-900 truncate">{k.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 btn-primary disabled:opacity-50"
                  data-testid="submit-admin-btn"
                >
                  {creating ? 'Inviting…' : 'Send invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== Danger Zone — System Reset ===== */}
      <div className="mt-10 sm:mt-14 rounded-2xl border border-red-200 bg-red-50/40 p-5 sm:p-6" data-testid="system-reset-zone">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Danger Zone — System Reset</h3>
            <p className="text-red-900/70 text-sm mt-1">
              Wipe ALL transactional data: booths, leads, orders, prints, photos, inventory, templates, and adjustment logs.
              Use this once before going live to clear test data. This action <span className="font-medium">cannot be undone</span>.
            </p>
          </div>
        </div>
        <button
          onClick={() => setResetOpen(true)}
          className="px-4 py-2.5 bg-white text-red-700 border border-red-300 rounded-xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2 font-medium"
          data-testid="open-system-reset-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Reset all data…
        </button>
      </div>

      {/* 3-step Reset Modal */}
      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/70 backdrop-blur-sm" data-testid="system-reset-modal">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 w-12 rounded-full transition-colors ${s <= resetStep ? 'bg-red-500' : 'bg-charcoal-900/10'}`}
                  data-testid={`reset-step-indicator-${s}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-red-600 font-medium">Step {resetStep} of 3</p>
                <h3 className="font-serif text-xl sm:text-2xl text-charcoal-900">
                  {resetStep === 1 && 'Reset all data?'}
                  {resetStep === 2 && 'Type the confirmation phrase'}
                  {resetStep === 3 && 'Confirm with your email'}
                </h3>
              </div>
            </div>

            {/* Step 1: warning + acknowledgement */}
            {resetStep === 1 && (
              <div data-testid="reset-step-1">
                <p className="text-charcoal-800/70 text-sm mb-3">
                  This will permanently delete:
                </p>
                <ul className="text-sm text-charcoal-800/70 space-y-1 mb-5 pl-5 list-disc">
                  <li>All booths and their pairing codes</li>
                  <li>All leads / venue inquiries</li>
                  <li>All payment orders &amp; revenue history</li>
                  <li>All print logs &amp; print history</li>
                  <li>All photo sessions (download links will break)</li>
                  <li>All inventory + adjustment audit logs</li>
                  <li>All print templates &amp; pricing settings</li>
                </ul>

                <label className="flex items-start gap-3 p-3 rounded-xl border border-charcoal-900/10 cursor-pointer hover:bg-cream-50 transition-colors mb-3">
                  <input
                    type="checkbox"
                    checked={resetAck}
                    onChange={(e) => setResetAck(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-red-600"
                    data-testid="reset-ack-checkbox"
                  />
                  <span className="text-sm text-charcoal-900">
                    I understand this action is permanent and irreversible.
                  </span>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-xl border border-charcoal-900/10 cursor-pointer hover:bg-cream-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={resetWipeAdmins}
                    onChange={(e) => setResetWipeAdmins(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-red-600"
                    data-testid="reset-wipe-admins-checkbox"
                  />
                  <span className="text-sm text-charcoal-900">
                    Also remove all non-super-admin users (your <em>super admin</em> account is always preserved)
                  </span>
                </label>

                <div className="flex gap-3 mt-6">
                  <button onClick={closeReset} className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors" data-testid="reset-cancel-btn">
                    Cancel
                  </button>
                  <button
                    onClick={() => setResetStep(2)}
                    disabled={!resetAck}
                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="reset-step-1-next-btn"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: phrase */}
            {resetStep === 2 && (
              <div data-testid="reset-step-2">
                <p className="text-charcoal-800/70 text-sm mb-3">
                  Type the phrase below exactly to continue:
                </p>
                <p className="font-mono text-charcoal-900 bg-cream-50 border border-charcoal-900/10 rounded-lg px-3 py-2 mb-3 text-sm select-none text-center">
                  RESET ALL DATA
                </p>
                <input
                  type="text"
                  value={resetPhrase}
                  onChange={(e) => setResetPhrase(e.target.value)}
                  placeholder="Type the phrase here"
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white border border-charcoal-900/15 rounded-xl focus:outline-none focus:border-red-400 transition-colors font-mono"
                  data-testid="reset-phrase-input"
                />
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setResetStep(1)} className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors" data-testid="reset-step-2-back-btn">
                    Back
                  </button>
                  <button
                    onClick={() => setResetStep(3)}
                    disabled={resetPhrase.trim() !== 'RESET ALL DATA'}
                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="reset-step-2-next-btn"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: email confirmation (post Google-auth migration — passwords no longer exist) */}
            {resetStep === 3 && (
              <div data-testid="reset-step-3">
                <p className="text-charcoal-800/70 text-sm mb-3">
                  Final step. Re-type your own admin email{' '}
                  <span className="font-medium text-charcoal-900">{user?.email || 'admin'}</span>{' '}
                  exactly to confirm:
                </p>
                <input
                  type="email"
                  value={resetEmailConfirm}
                  onChange={(e) => setResetEmailConfirm(e.target.value)}
                  placeholder="Your admin email"
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white border border-charcoal-900/15 rounded-xl focus:outline-none focus:border-red-400 transition-colors"
                  data-testid="reset-email-confirm-input"
                />
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setResetStep(2)} disabled={resetting} className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors disabled:opacity-50" data-testid="reset-step-3-back-btn">
                    Back
                  </button>
                  <button
                    onClick={performReset}
                    disabled={
                      resetting ||
                      !resetEmailConfirm ||
                      resetEmailConfirm.trim().toLowerCase() !== (user?.email || '').toLowerCase()
                    }
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                    data-testid="reset-confirm-btn"
                  >
                    {resetting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Wipe everything
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
